import React, { useCallback, useMemo } from 'react';
import { Button, Modal, Table, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { AiChatRow } from 'services/api/aiChatService';

const Sql = styled.pre`
  background: #f0f0f0;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.5;
  color: #333;
  margin: 0 0 12px;
  max-height: 140px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// RFC 4180: wrap a field in quotes if it contains a comma, quote, or newline,
// and double any inner quote. Nulls become empty cells rather than the table's em dash.
const csvEscape = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const triggerCsvDownload = (filename: string, csvContent: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const compareCell = (a: unknown, b: unknown): number => {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return formatCell(a).localeCompare(formatCell(b), undefined, { numeric: true });
};

interface Props {
  open: boolean;
  rows: AiChatRow[];
  sql?: string;
  /** Total rows the query matched — may be more than `rows.length`. */
  rowCount?: number;
  onClose: () => void;
}

const QueryResultsModal: React.FC<Props> = ({ open, rows, sql, rowCount, onClose }) => {
  // Columns come from the rows themselves, so any query shape renders.
  const keys = useMemo(() => {
    const found: string[] = [];
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!found.includes(key)) found.push(key);
      });
    });
    return found;
  }, [rows]);

  const columns = useMemo<ColumnsType<AiChatRow>>(
    () => keys.map((key) => ({
      title: key,
      dataIndex: key,
      key,
      ellipsis: true,
      sorter: (a: AiChatRow, b: AiChatRow) => compareCell(a[key], b[key]),
      render: (value: unknown) => formatCell(value),
    })),
    [keys],
  );

  const total = rowCount ?? rows.length;
  const truncated = total > rows.length;

  const downloadCsv = useCallback(() => {
    const csv = [
      keys.map(csvEscape).join(','),
      ...rows.map((row) => keys.map((key) => csvEscape(row[key])).join(',')),
    ].join('\r\n');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    triggerCsvDownload(`query-results-${stamp}.csv`, csv);
  }, [keys, rows]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(1100px, 92vw)"
      title={`Query results — ${total} row${total === 1 ? '' : 's'}${truncated ? ` (showing first ${rows.length})` : ''}`}
      destroyOnClose
    >
      {sql && <Sql>{sql}</Sql>}
      <Toolbar>
        <Button size="small" icon={<DownloadOutlined />} onClick={downloadCsv} disabled={!rows.length}>
          Download CSV
        </Button>
      </Toolbar>
      <Table<AiChatRow>
        columns={columns}
        dataSource={rows}
        rowKey={(_, index) => String(index)}
        size="small"
        bordered
        scroll={{ x: 'max-content', y: '50vh' }}
        pagination={rows.length > 20 ? { pageSize: 20, showSizeChanger: true } : false}
      />
      {truncated && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Only the first {rows.length} rows are shown. Narrow the question to see the rest.
        </Typography.Text>
      )}
    </Modal>
  );
};

export default QueryResultsModal;
